import React, {
  useEffect,
  useState,
  useCallback,
  useImperativeHandle,
  forwardRef,
  useRef,
} from "react";
import {
  MapContainer,
  TileLayer,
  GeoJSON,
  Marker,
  Popup,
  useMap,
  ZoomControl,
} from "react-leaflet";
import L from "leaflet";
import { GeoJsonData, Municipality } from "../types";
import axios from "axios";
import {
  loadBudgetData,
  ParsedBudgetData,
  findBudgetByMatchKey,
} from "../services/budgetDataService";

// Convert coordinates from Web Mercator (EPSG:3857) to WGS84 (EPSG:4326)
function convertToLatLng(x: number, y: number): [number, number] {
  // Convert from Web Mercator (EPSG:3857) to WGS84 (EPSG:4326)
  const lon = (x * 180) / 20037508.34;
  const lat =
    (Math.atan(Math.exp((y * Math.PI) / 20037508.34)) * 360) / Math.PI - 90;
  return [lon, lat];
}

// Fix for Leaflet marker icons
// @ts-ignore
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png",
});

interface MapProps {
  onMunicipalitySelect: (municipality: Municipality | null) => void;
  selectedYear: number;
}

// Export interface for map ref methods
export interface MapRef {
  focusOnMunicipality: (municipalityId: string) => void;
}

// Component to handle map recenter when location changes
const LocationMarker: React.FC<{ position: [number, number] }> = ({
  position,
}) => {
  const map = useMap();

  useEffect(() => {
    map.flyTo(position, 13);
  }, [position, map]);

  // Using a custom location marker with pulsing effect
  return (
    <Marker
      position={position}
      icon={L.divIcon({
        className: "custom-marker-icon",
        html: '<div class="marker-pin"></div><div class="pulse"></div>',
        iconSize: [30, 30],
        iconAnchor: [15, 30],
      })}
    >
      <Popup className="custom-popup">
        <div className="popup-content">
          <strong>คุณอยู่ที่นี่</strong>
          <p>นี่คือตำแหน่งปัจจุบันของคุณ</p>
        </div>
      </Popup>
    </Marker>
  );
};

const Map = forwardRef<MapRef, MapProps>(
  ({ onMunicipalitySelect, selectedYear }, ref) => {
    const [geoJsonData, setGeoJsonData] = useState<GeoJsonData | null>(null);
    const [budgetData, setBudgetData] = useState<ParsedBudgetData[]>([]);
    const [userLocation, setUserLocation] = useState<[number, number] | null>(
      null
    );
    const [loading, setLoading] = useState<boolean>(false);
    const [dataLoading, setDataLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [mapLayers, setMapLayers] = useState<{ [key: string]: L.Layer }>({});
    const [mapInstance, setMapInstance] = useState<L.Map | null>(null);
    const layersRef = useRef<{ [key: string]: L.Layer }>({});

    // Default center coordinates for Thailand
    const defaultCenter: [number, number] = [13.7563, 100.5018]; // Bangkok coordinates

    // Style function for GeoJSON features (all same color)
    const style = useCallback(() => {
      return {
        fillColor: "#00D2CA", // OBT color (teal)
        weight: 2,
        opacity: 0.9,
        color: "#FFFFFF",
        dashArray: "",
        fillOpacity: 0.7,
      };
    }, []);

    // Store layer references without triggering re-renders
    const storeLayerRef = useCallback((id: string, layer: L.Layer) => {
      layersRef.current[id] = layer;
    }, []);

    // Once GeoJSON data is loaded and rendered, update the mapLayers state
    useEffect(() => {
      if (geoJsonData && Object.keys(layersRef.current).length > 0) {
        setMapLayers(layersRef.current);
      }
    }, [geoJsonData]);

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      focusOnMunicipality: (municipalityId: string) => {
        // Find the layer for the municipality ID
        const layer =
          layersRef.current[municipalityId] || mapLayers[municipalityId];
        if (layer && mapInstance) {
          // Since TypeScript doesn't recognize getBounds on all layer types,
          // we need to use type assertions to access the method
          try {
            // Try to access getBounds and fit bounds
            if ("getBounds" in layer) {
              const bounds = (layer as any).getBounds();
              mapInstance.fitBounds(bounds);
            }

            // Simulate click on the layer to update styles
            if ("fire" in layer) {
              (layer as any).fire("click");
            }
          } catch (error) {
            console.error("Error focusing on municipality:", error);
          }
        } else {
          console.warn(
            `Municipality with ID ${municipalityId} not found in map layers`
          );
        }
      },
    }));

    // Function to handle interaction with each GeoJSON feature
    const onEachFeature = useCallback(
      (feature: any, layer: L.Layer) => {
        if (feature.properties) {
          // Store reference to the layer with municipality ID as key
          const municipalityId =
            feature.properties.id ||
            feature.properties.muni_code ||
            feature.properties.name;

          // Store layer reference in ref instead of setState
          storeLayerRef(municipalityId, layer);

          // Style change on hover
          layer.on({
            mouseover: (e) => {
              const layer = e.target as L.Path;
              layer.setStyle({
                weight: 3,
                fillOpacity: 0.9,
              });
              if (layer.bringToFront) {
                layer.bringToFront();
              }
            },
            mouseout: (e) => {
              const layer = e.target as L.Path;
              layer.setStyle(style());
            },
            click: (e) => {
              // Get the match key from feature properties
              const matchKey = feature.properties.id;

              // Find budget data for this OBT
              const budget = findBudgetByMatchKey(budgetData, matchKey);

              // Create municipality data object from feature properties and budget data
              const municipalityData: Municipality = {
                id: matchKey || "unknown",
                name: budget?.name || feature.properties.name || "ไม่ทราบชื่อ",
                budget: budget ? budget.totalRevenue * 1000000 : 0, // Convert from million baht to baht
                province:
                  budget?.province ||
                  feature.properties.province ||
                  "ไม่ทราบจังหวัด",
                district:
                  budget?.district ||
                  feature.properties.district ||
                  "ไม่ทราบอำเภอ",
                type: "องค์การบริหารส่วนตำบล", // OBT type
                population: budget?.population || 0,
                area: budget?.area || 0,
                budgetSources: budget
                  ? {
                      selfCollected: budget.selfCollected,
                      stateAllocated: budget.stateAllocated,
                      subsidies: budget.subsidies,
                    }
                  : undefined,
              };

              // Send municipality data to parent component to show in sidebar
              onMunicipalitySelect(municipalityData);

              // Zoom to municipality bounds
              if (e.target.getBounds) {
                const bounds = e.target.getBounds();
                if (bounds) {
                  const mapInstance = e.target._map;
                  if (mapInstance) {
                    mapInstance.fitBounds(bounds);
                  }
                }
              }
            },
          });
        }
      },
      [storeLayerRef, onMunicipalitySelect, style, budgetData]
    );

    // Get the map instance when it's ready
    const MapController = () => {
      const map = useMap();

      // Store map instance
      useEffect(() => {
        setMapInstance(map);
      }, [map]);

      return null;
    };

    // Load GeoJSON data when component mounts
    useEffect(() => {
      const loadGeoJsonData = async () => {
        try {
          setDataLoading(true);
          console.log("Loading GeoJSON data...");

          // Load the exclusion list of upgraded OBT (now เทศบาล)
          let exclusionList: Array<{tambon: string; amphoe: string; province: string}> = [];
          try {
            const exclusionResponse = await axios.get(
              `${process.env.PUBLIC_URL}/data/upgraded-obt-exclusion.json`
            );
            exclusionList = exclusionResponse.data.excludedOBT || [];
            console.log(`Loaded ${exclusionList.length} upgraded OBT to exclude`);
          } catch (err) {
            console.warn("Could not load exclusion list, showing all OBT");
          }

          // Load the obt_polygons.json file - add a timestamp query parameter to force reload
          const response = await axios.get(
            `${
              process.env.PUBLIC_URL
            }/data/obt_polygons.json?t=${new Date().getTime()}`
          );

          console.log("GeoJSON data loaded successfully");

          // Debugging the raw data structure
          console.log("Raw GeoJSON structure:", Object.keys(response.data));

          // Check if the data is a valid GeoJSON structure
          if (response.data.type !== "FeatureCollection") {
            console.error("Data is not a valid GeoJSON FeatureCollection");
            setError("ข้อมูลไม่ถูกต้อง ไม่ใช่ GeoJSON ที่ถูกต้อง");
            setDataLoading(false);
            return;
          }

          const geoJsonData = response.data;

          console.log("Number of features:", geoJsonData.features?.length || 0);

          if (!geoJsonData.features || geoJsonData.features.length === 0) {
            console.error("GeoJSON has no features");
            setError("ข้อมูลไม่มีลักษณะพื้นที่ (features)");
            setDataLoading(false);
            return;
          }

          // Log the first feature's geometry type and coordinates
          const firstFeature = geoJsonData.features[0];
          if (firstFeature.geometry) {
            console.log(
              "First feature geometry type:",
              firstFeature.geometry.type
            );

            // Check if coordinates exist and log a sample
            if (firstFeature.geometry.coordinates) {
              console.log(
                "First feature coordinates sample:",
                JSON.stringify(firstFeature.geometry.coordinates).substring(
                  0,
                  100
                ) + "..."
              );
            }
          } else {
            console.warn("First feature has null geometry");
          }

          // Transform the coordinates from Web Mercator to WGS84 (latitude and longitude)
          const transformedFeatures = geoJsonData.features.map(
            (feature: any) => {
              const newFeature = { ...feature };

              // Skip transformation for features with null geometry
              if (!feature.geometry) {
                console.warn("Feature has null geometry:", feature);
                return newFeature;
              }

              if (feature.geometry.type === "Polygon") {
                newFeature.geometry = {
                  ...feature.geometry,
                  coordinates: feature.geometry.coordinates.map(
                    (ring: any[]) => {
                      return ring.map((coord: number[]) =>
                        convertToLatLng(coord[0], coord[1])
                      );
                    }
                  ),
                };
              } else if (feature.geometry.type === "MultiPolygon") {
                newFeature.geometry = {
                  ...feature.geometry,
                  coordinates: feature.geometry.coordinates.map(
                    (polygon: any[][]) => {
                      return polygon.map((ring: any[]) => {
                        return ring.map((coord: number[]) =>
                          convertToLatLng(coord[0], coord[1])
                        );
                      });
                    }
                  ),
                };
              }

              return newFeature;
            }
          );

          if (transformedFeatures[0]?.geometry?.coordinates) {
            console.log(
              "First transformed feature coordinates:",
              transformedFeatures[0].geometry.coordinates[0][0]
            );
          }

          // Filter out upgraded OBT (now เทศบาล) from the features
          const normalizeText = (text: string) => 
            text?.replace(/\s+/g, '').replace(/จังหวัด|อำเภอ|ตำบล/g, '').toLowerCase() || '';
          
          const filteredFeatures = transformedFeatures.filter((feature: any) => {
            const props = feature.properties || {};
            // Get tambon name from feature - try different property names
            const featureTambon = normalizeText(props.name || props.tam_name || props.tambon || '');
            const featureAmphoe = normalizeText(props.amp_name || props.amphoe || props.district || '');
            const featureProvince = normalizeText(props.cwt_name || props.province || '');
            
            // Check if this feature is in the exclusion list
            const isExcluded = exclusionList.some(excluded => {
              const excludedTambon = normalizeText(excluded.tambon);
              const excludedAmphoe = normalizeText(excluded.amphoe);
              const excludedProvince = normalizeText(excluded.province);
              
              // Match by tambon name, amphoe, and province
              const tambonMatch = featureTambon.includes(excludedTambon) || excludedTambon.includes(featureTambon);
              const amphoeMatch = featureAmphoe.includes(excludedAmphoe) || excludedAmphoe.includes(featureAmphoe);
              const provinceMatch = featureProvince.includes(excludedProvince) || excludedProvince.includes(featureProvince);
              
              return tambonMatch && amphoeMatch && provinceMatch;
            });
            
            if (isExcluded) {
              console.log(`Excluding upgraded OBT: ${props.name || props.tam_name} (${featureAmphoe}, ${featureProvince})`);
            }
            
            return !isExcluded;
          });

          console.log(`Filtered features: ${filteredFeatures.length} (excluded ${transformedFeatures.length - filteredFeatures.length} upgraded OBT)`);

          // Set the GeoJSON data with transformed and filtered coordinates
          setGeoJsonData({
            type: "FeatureCollection",
            features: filteredFeatures,
          } as GeoJsonData);
          setDataLoading(false);
        } catch (err) {
          console.error("Error loading GeoJSON data:", err);
          console.error("Error details:", {
            message: err instanceof Error ? err.message : "Unknown error",
            stack: err instanceof Error ? err.stack : undefined,
            response:
              err && (err as any).response
                ? (err as any).response.data
                : undefined,
          });
          setError("ไม่สามารถโหลดข้อมูลแผนที่ได้ กรุณาลองใหม่อีกครั้ง");
          setDataLoading(false);
        }
      };

      loadGeoJsonData();
    }, []);

    // Load budget data when component mounts or year changes
    useEffect(() => {
      const loadBudgetDataForYear = async () => {
        try {
          console.log(`Loading budget data for year ${selectedYear}...`);
          const data = await loadBudgetData(selectedYear);
          console.log(`Budget data loaded: ${data.length} records`);
          setBudgetData(data);
        } catch (err) {
          console.error("Error loading budget data:", err);
          // Don't show error to user, budget data is supplementary
        }
      };

      loadBudgetDataForYear();
    }, [selectedYear]);

    const findNearestMunicipality = useCallback(
      (location: [number, number]) => {
        if (
          !geoJsonData ||
          !geoJsonData.features ||
          !geoJsonData.features.length
        ) {
          setError("ไม่พบข้อมูลอบต. กรุณาลองใหม่อีกครั้ง");
          return;
        }

        // In a real application, you would implement a more efficient algorithm
        // This is a simplified version that checks if the point is within a municipality boundary

        console.log(
          `Searching for municipality at coordinates: ${location[0]},${location[1]}`
        );
        console.log(
          `Total municipalities to check: ${geoJsonData.features.length}`
        );

        let foundMunicipality = false;

        for (const feature of geoJsonData.features) {
          try {
            const geoJsonLayer = L.geoJSON(feature as any);
            const latLng = L.latLng(location[0], location[1]);

            // Check if the user's location is within this municipality
            if (geoJsonLayer.getBounds().contains(latLng)) {
              console.log(`Found OBT: ${feature.properties.name}`);
              foundMunicipality = true;

              // Get the match key from feature properties
              const matchKey = feature.properties.id;

              // Find budget data for this OBT
              const budget = findBudgetByMatchKey(budgetData, matchKey);

              const municipalityData: Municipality = {
                id: matchKey || "unknown",
                name: budget?.name || feature.properties.name || "ไม่ทราบชื่อ",
                budget: budget ? budget.totalRevenue * 1000000 : 0,
                province:
                  budget?.province ||
                  feature.properties.province ||
                  "ไม่ทราบจังหวัด",
                district:
                  budget?.district ||
                  feature.properties.district ||
                  "ไม่ทราบอำเภอ",
                type: "องค์การบริหารส่วนตำบล",
                population: budget?.population || 0,
                area: budget?.area || 0,
                budgetSources: budget
                  ? {
                      selfCollected: budget.selfCollected,
                      stateAllocated: budget.stateAllocated,
                      subsidies: budget.subsidies,
                    }
                  : undefined,
              };

              onMunicipalitySelect(municipalityData);
              return;
            }
          } catch (err) {
            console.error("Error checking municipality boundary:", err);
            // Continue to the next feature
          }
        }

        if (!foundMunicipality) {
          console.log("No municipality found at the given location");
          setError("ไม่พบองค์การบริหารส่วนตำบลในตำแหน่งของคุณ");
          onMunicipalitySelect(null);
        }
      },
      [geoJsonData, budgetData, onMunicipalitySelect]
    );

    const handleGetLocation = useCallback(() => {
      setLoading(true);
      setError(null);

      if (!navigator.geolocation) {
        setError("เบราว์เซอร์ของคุณไม่รองรับการระบุตำแหน่ง");
        setLoading(false);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation([latitude, longitude]);
          findNearestMunicipality([latitude, longitude]);
          setLoading(false);
        },
        (err) => {
          console.error("Error getting location:", err);
          setError(
            "ไม่สามารถระบุตำแหน่งของคุณได้ กรุณาอนุญาตการเข้าถึงตำแหน่ง"
          );
          setLoading(false);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    }, [findNearestMunicipality]);

    return (
      <div className="map-component">
        <div className="map-controls">
          <button onClick={handleGetLocation} disabled={loading || dataLoading}>
            {loading ? (
              <>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="loading-icon"
                >
                  <path
                    d="M12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C22 9.27455 20.9097 6.80375 19.1414 5"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    stroke="currentColor"
                  />
                </svg>
                กำลังค้นหา...
              </>
            ) : dataLoading ? (
              <>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="loading-icon"
                >
                  <path
                    d="M12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C22 9.27455 20.9097 6.80375 19.1414 5"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    stroke="currentColor"
                  />
                </svg>
                กำลังโหลดข้อมูล...
              </>
            ) : (
              <>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 12V21"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <path
                    d="M12 12L16.5 7.5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <path
                    d="M12 12L7.5 7.5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <circle
                    cx="12"
                    cy="5"
                    r="3"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                </svg>
                ค้นหาอบต.ใกล้ฉัน
              </>
            )}
          </button>
          {error && (
            <div className="error-message">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path
                  d="M12 7V13"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <circle cx="12" cy="16" r="1" fill="currentColor" />
              </svg>
              {error}
            </div>
          )}
          {dataLoading && (
            <div className="info-message">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path
                  d="M12 7V13"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <circle cx="12" cy="16" r="1" fill="currentColor" />
              </svg>
              กำลังโหลดข้อมูลอบต. กรุณารอสักครู่...
            </div>
          )}
        </div>

        <div className="map-container">
          <MapContainer
            center={defaultCenter}
            zoom={6}
            zoomControl={false}
            minZoom={5}
            maxBounds={[
              [5, 96], // Southwest coordinates
              [21, 106], // Northeast coordinates
            ]}
            maxBoundsViscosity={1.0} // Prevents dragging outside of bounds
          >
            <MapController />
            <ZoomControl position="bottomright" />
            <TileLayer
              attribution='&copy; <a href="https://carto.com/attributions">CARTO</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
              maxZoom={19}
            />
            {userLocation && <LocationMarker position={userLocation} />}
            {geoJsonData && (
              <GeoJSON
                key={`geojson-${Date.now()}`} // Add key to force re-render when data changes
                data={geoJsonData}
                style={style}
                onEachFeature={onEachFeature}
              />
            )}

            <div className="map-legend">
              <div className="map-legend-title">องค์กรปกครองส่วนท้องถิ่น</div>
              <div className="map-legend-item">
                <div
                  className="map-legend-color"
                  style={{ backgroundColor: "#00D2CA" }}
                ></div>
                <span>องค์การบริหารส่วนตำบล (อบต.)</span>
              </div>
            </div>
          </MapContainer>
        </div>
      </div>
    );
  }
);

export default Map;
